import { useState } from 'react';
import { useQuery, useMutation, useIsMutating } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faSearch, faX } from '@fortawesome/free-solid-svg-icons';
import { isEmpty } from 'lodash';
import { withAppHOC } from '../../hoc';
import {
  StyledContentContainer,
  StyledFiltersContainer,
  StyledFilters,
  StyledFilterContainer,
  StyledSearchButton,
  StyledResetButton,
  StyledStatusPill,
  StyledActionItemButtons,
  StyledEditItemButton,
} from '../../components/styled';
import PageHeader from '../../components/PageHeader/PageHeader';
import {
  Switch,
  MessageBox,
  SearchInput,
  CustomConfirmButton,
} from '../../components/FormElements';
import Loader from '../../components/Loader/Loader';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import NoData from '../../components/NoData/NoData';
import { TableBuilder } from '../../components/TableElements';
import Pagination from '../../components/Pagination/Pagination';
import API from '../../api';
import { API_CONFIG, RESPONSE_STATUSES } from '../../constants';
import { formatCurrency, getAuthData, getSearchParams } from '../../utils';
import FiltersButton from '../../components/FiltersButton/FiltersButton';

const SellerAPISettingsList = () => {
  const [showFilters, setShowFilters] = useState(false);
  const onClickShowFilters = () => {
    setShowFilters((showFilters) => !showFilters);
  };
  const {
    userProfile: { sellersAPIEditStatus, sellersAPIDeleteStatus, canEditApis },
  } = getAuthData();
  const { API_BASE_URL } = API_CONFIG;
  const [filters, setFilters] = useState({
    searchQuery: '',
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [paginationData, setPaginationData] = useState({
    pageNumber: getSearchParams('pageNumber') || 1,
    recordsPerPage: getSearchParams('recordsPerPage') || 30,
  });

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const readData = async () => {
    try {
      const response = await API.get(
        `/seller/api-settings?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&searchQuery=${appliedFilters.searchQuery}&fetchBalance=Yes`
      );
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const updateData = async ({ value, newData }) => {
    try {
      newData.apiStatus = value === true ? `Active` : `Inactive`;
      const response = await API.put(
        `/seller/api-settings/${newData.id}?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&searchQuery=${appliedFilters.searchQuery}&fetchBalance=Yes`,
        newData
      );
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const deleteData = async (id) => {
    try {
      const response = await API.delete(
        `/seller/api-settings/${id}?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&searchQuery=${appliedFilters.searchQuery}&fetchBalance=Yes`
      );
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ['seller_api_settings', paginationData, appliedFilters],
    queryFn: readData,
  });

  const reloadData = async () => await refetch();

  const { mutateAsync: updateDataMutation } = useMutation({
    mutationFn: updateData,
    onError: (error) => console.error(error),
    onSuccess: () => {
      reloadData();
    },
  });

  const { mutateAsync: deleteDataMutation } = useMutation({
    mutationFn: deleteData,
    onError: (error) => console.error(error),
    onSuccess: () => {
      reloadData();
    },
  });

  const isMutation = useIsMutating();

  const filterData = async (e) => {
    e.preventDefault();
    setAppliedFilters(filters);
  };

  const clearFilters = async (e) => {
    e.preventDefault();
    setFilters({
      searchQuery: '',
    });
    setAppliedFilters({
      searchQuery: '',
    });
  };

  const renderPageHeading = () => (
    <PageHeader
      title="API Settings"
      showAddButton={true}
      addLink="create"
      addTitle="Add API"
      showReloadData={true}
      reloadData={reloadData}
    />
  );

  if (isLoading) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <LoadingSpinner />
        </StyledContentContainer>
      </>
    );
  }

  if (error) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={error.message}
          />
        </StyledContentContainer>
      </>
    );
  }

  return (
    <>
      <Loader isLoading={isMutation > 0 || isRefetching} />
      {renderPageHeading()}
      <FiltersButton onClick={onClickShowFilters} value={showFilters} />
      <StyledContentContainer graybackground="true">
        <StyledFiltersContainer className={showFilters ? 'show' : ''}>
          <StyledFilters>
            <StyledFilterContainer>
              <SearchInput
                value={filters.searchQuery}
                onChange={(value) => handleFilterChange('searchQuery', value)}
                disabled={isLoading}
                placeholder="Search API Name"
              />
            </StyledFilterContainer>
            <StyledSearchButton onClick={filterData} type="button">
              <FontAwesomeIcon icon={faSearch} />
              Search
            </StyledSearchButton>
            <StyledResetButton onClick={clearFilters} type="button">
              <FontAwesomeIcon icon={faX} />
              Clear
            </StyledResetButton>
          </StyledFilters>
        </StyledFiltersContainer>
        {data?.status === RESPONSE_STATUSES.FAILED && (
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={data?.message}
          />
        )}
        {data?.data?.length === 0 ? (
          <NoData />
        ) : (
          <>
            <TableBuilder
              isLoading={isLoading}
              tableHeadings={[
                {
                  title: 'API Name',
                  dataSelector: 'apiName',
                  dataType: 'string',
                },
                {
                  title: 'IP Address',
                  dataSelector: 'ipAddress',
                  dataType: 'string',
                  cellrenderer: (value) => value || 'N/A',
                },
                {
                  title: 'Callback URL',
                  dataSelector: 'callbackKey',
                  dataType: 'string',
                  cellrenderer: (value) => (
                    <div style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
                      {`${API_BASE_URL}/callback/${value}`}
                    </div>
                  )
                },
                {
                  title: 'Balance',
                  dataSelector: 'balance',
                  dataType: 'number',
                  cellrenderer: (value) => formatCurrency(value),
                },
                {
                  title: 'Status',
                  dataSelector: 'apiStatus',
                  dataType: 'string',
                  canSort: false,
                  textAlign: 'center',
                  cellrenderer: (value, data) =>
                    !isEmpty(value) && (
                      <Switch
                        value={value === 'Active'}
                        onChange={async (value) =>
                          await updateDataMutation({
                            value,
                            newData: { ...data },
                          })
                        }
                      />
                    ),
                },
                {
                  title: 'Admin Status',
                  dataSelector: 'adminsApiStatus',
                  dataType: 'string',
                  textAlign: 'center',
                  cellrenderer: (value) => (
                    <StyledStatusPill
                      className={value === 'Active' ? 'green' : 'red'}
                    >
                      {value}
                    </StyledStatusPill>
                  ),
                },
                {
                  title: 'Remark',
                  dataSelector: 'remark',
                  dataType: 'string',
                  cellrenderer: (value) => value || 'N/A',
                },
                {
                  title: '',
                  dataSelector: 'id',
                  canSort: false,
                  isSticky: 'Yes',
                  stickyAlign: 'right',
                  isDummy: 'Yes',
                  textAlign: 'center',
                  width: 'max-content',
                  cellrenderer: (value, item) => (
                    <StyledActionItemButtons>
                      {((sellersAPIEditStatus &&
                        sellersAPIEditStatus === 'Yes') ||
                        canEditApis === 'Active') && (
                          <StyledEditItemButton
                            to={`update/${value}`}
                            title="Update API"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </StyledEditItemButton>
                        )}
                      {sellersAPIDeleteStatus &&
                        sellersAPIDeleteStatus === 'Yes' && (
                          <CustomConfirmButton
                            showTitle={false}
                            onClick={async () =>
                              await deleteDataMutation(value)
                            }
                          />
                        )}
                    </StyledActionItemButtons>
                  ),
                },
              ]}
              tableData={data?.data}
            />
            <Pagination
              totalRecords={data?.totalRecords}
              paginationData={paginationData}
              setPaginationData={setPaginationData}
            />
          </>
        )}
      </StyledContentContainer>
    </>
  );
};

export default withAppHOC(SellerAPISettingsList);
